import { useSyncExternalStore } from 'react';
import { TABLET, type Arrangement } from '~/table/cross/geometry.js';

/**
 * Which of the two arrangements a full-screen surface is drawing.
 *
 * In `ui/` because Rules, Scoring, the Learn header and Classic's game over
 * and review read it as well as Cross's after-match screens.
 *
 * The sketches are two fixed frames and the product is one layout with two
 * arrangements, split at the `tablet` breakpoint: below it the phone stack,
 * at it and above the laptop one. Most of that split is CSS, but the after-
 * match screens carry measured numbers a class cannot say -- a 46-point final
 * against a 60-point one, a 38-wide strip tile against a 44-wide one -- and
 * those have to be read in TypeScript to be checked against the sketch's own
 * tables.
 *
 * A media query rather than a `ResizeObserver`, because these screens take the
 * whole viewport: there is no parent whose width could differ from the
 * window's. The board's geometry is the other case and it measures its own box.
 *
 * `useSyncExternalStore` rather than an effect, so the first paint is already
 * right on a laptop instead of drawing the phone stack and correcting it. Off
 * the browser -- a static render in a test -- there is no `matchMedia`, and the
 * phone arrangement is the honest default: it is the one every viewport gets
 * until something says otherwise.
 */

export type { Arrangement };

/** `TABLET`, which matches `--breakpoint-tablet` in `ui/styles.css`. */
const LAPTOP = `(min-width: ${TABLET}px)`;

function subscribe(onChange: () => void): () => void {
  const query = globalThis.matchMedia?.(LAPTOP);
  query?.addEventListener('change', onChange);
  return () => {
    query?.removeEventListener('change', onChange);
  };
}

function read(): Arrangement {
  const wide = globalThis.matchMedia?.(LAPTOP).matches ?? false;
  return wide ? 'laptop' : 'phone';
}

export function useArrangement(): Arrangement {
  return useSyncExternalStore(subscribe, read, () => 'phone');
}
